<script setup lang="ts">
import type { CookieTypeConfig } from '../../types/cookies';
import { computed, ref } from 'vue';
import ButtonLink from '../ButtonLink.vue';
import Switch from '../Switch.vue';

const props = withDefaults(defineProps<{
	config: CookieTypeConfig;
	enabled?: boolean;
}>(), {
	enabled: false,
});

const emit = defineEmits<{
	'update:enabled': [value: boolean];
}>();

const showInfo = ref(false);
const toggleInfo = () => {
	showInfo.value = !showInfo.value;
};

const enabledModel = computed({
	get: () => (props.config.required ? true : props.enabled),
	set: (value: boolean) => {
		if (props.config.required) {
			return;
		}
		emit('update:enabled', value);
	},
});
</script>

<template>
	<div class="cookie-settings-item">
		<div class="cookie-settings-item__main">
			<div class="cookie-settings-item__content">
				<span class="cookie-settings-item__title">
					{{ props.config.title }}
				</span>
				<p class="cookie-settings-item__description">
					{{ props.config.description }}
				</p>
				<ButtonLink
					:label="showInfo ? 'Hide details' : 'Show details'"
					size="sm"
					@click="toggleInfo()"
				/>
			</div>
			<div class="cookie-settings-item__actions">
				<Switch
					:checked="enabledModel"
					:disabled="props.config.required"
					v-model="enabledModel"
				/>
			</div>
		</div>

		<div class="cookie-settings-item__info" v-if="showInfo">
			<table class="cookie-settings-item__table">
				<thead>
					<tr>
						<th><span>Cookie name</span></th>
						<th><span>Duration</span></th>
						<th><span>Description</span></th>
					</tr>
				</thead>
				<tbody>
					<tr
						v-for="cookie in props.config.cookies"
						:key="cookie.name"
					>
						<td>{{ cookie.name }}</td>
						<td>{{ cookie.duration }}</td>
						<td>{{ cookie.description }}</td>
					</tr>
				</tbody>
			</table>
		</div>
	</div>
</template>

<style scoped lang="scss">
@layer hm-components
{
	.cookie-settings-item
	{
		@include flex;
		flex-direction:column;
		border:1px solid var(--border-primary);
		border-radius:var(--radius-xl);
		background-color:var(--bg-primary);
		padding:var(--spacing-md) var(--spacing-lg);

		&__main
		{
			@include flex(null, start, var(--spacing-lg));
		}
		&__content
		{
			@include flex(null, start, var(--spacing-4xs));
			flex-direction:column;
		}
		&__title
		{
			color:var(--c-body);
			@include text-body(sm);
			font-weight:500;
		}
		&__description
		{
			color:var(--c-caption);
			@include text-caption;
		}
		&__info
		{
			max-width:100%;
			overflow-x:auto;
			padding-block:var(--spacing-lg);
		}
		&__table
		{
			width:100%;

			th
			{
				color:var(--c-subtitle);
				@include text-caption;
				white-space:nowrap;
				border:0;
				background-color:var(--bg-quaternary);
				padding:var(--spacing-4xs) var(--spacing-md);

				&:first-child
				{
					border-radius:var(--radius-md) 0 0 var(--radius-md);
				}
				&:last-child
				{
					min-width:20rem;
					border-radius:0 var(--radius-md) var(--radius-md) 0;
				}
			}
			td
			{
				@include text-caption;
				padding:var(--spacing-2xs) var(--spacing-md);
			}
		}
	}
}
</style>
